import {FormControl} from "@/components/ui/form";
import {
  Select as SchSelect,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/sch-select";
import {cn} from "@/lib/utils";
import {useMemo} from "react";
import {useFormContext} from "react-hook-form";

type TOption = {label: string; value: string};

type SelectProps = {
  value: string | undefined;
  className?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  options: TOption[] | {groupLabel: string; items: TOption[]}[];
  disabled?: boolean;
};

function flattenSelectOptions(
  options: SelectProps["options"],
): TOption[] {
  if (!options.length) return [];
  const first = options[0];
  if (first && "groupLabel" in first) {
    return (options as {groupLabel: string; items: TOption[]}[]).flatMap((g) => g.items);
  }
  return options as TOption[];
}

export default function Select({
  value,
  className,
  placeholder,
  onChange,
  options,
  disabled = false,
}: SelectProps) {
  const formContext = useFormContext();
  const flatOptions = useMemo(() => flattenSelectOptions(options), [options]);

  const trigger = (
    <SelectTrigger
      className={cn("w-full truncate", className)}
      disabled={!options.length || disabled}
    >
      <SelectValue placeholder={placeholder}>
        {(v) => {
          const raw = v as string | null | undefined;
          if (raw === null || raw === undefined || raw === "") {
            return placeholder ?? "";
          }
          const label = flatOptions.find((o) => o.value === raw)?.label;
          return label ?? raw;
        }}
      </SelectValue>
    </SelectTrigger>
  );

  return (
    <SchSelect
      value={value}
      onValueChange={(next) => {
        if (typeof next === "string" && next) onChange(next);
      }}
      // disabled={!options.length || disabled}
    >
      {formContext ? <FormControl>{trigger}</FormControl> : trigger}
      <SelectContent>
        {options.map((option) =>
          "groupLabel" in option ? (
            <SelectGroup key={`${option.groupLabel}`}>
              <SelectLabel>{option.groupLabel}</SelectLabel>
              {option.items.map((item) => (
                <SelectItem key={`${item.value}-${item.label}`} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ) : (
            <SelectItem key={`${option.value}-${option.label}`} value={option.value}>
              {option.label}
            </SelectItem>
          ),
        )}
      </SelectContent>
    </SchSelect>
  );
}
